import React, { useEffect } from 'react'
import { Prototype, useNavigation } from '@sketch-hq/sketch-prototype-player'
import { useHistory, useParams } from 'react-router-dom'
import { RouteParams } from '@sketch/constants'
import { useVersioning } from 'modules/versioning'
import * as Styles from './PrototypePlayer.styles'
import { noop } from '@sketch/utils'
import { useExitFullscreen } from './useExitFullscreen'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { usePrototypeViewContext } from '../../hooks'

export function PrototypePlayer() {
  useKeyboardShortcuts()
  useSyncPrototypeNavigationWithAppRouting()

  const { showExitFullscreenButton } = usePrototypeViewContext()
  const { onExitFullscreenButtonClick } = useExitFullscreen()

  return (
    <Styles.Container>
      {/* Empty onPointerDown callback here is a hack fix for https://github.com/sketch-hq/Cloud/issues/12997
      which we think is caused by a WebKit bug, or 3rd party code in cloud-frontend messing with touch events.
      Will keep an eye on it */}
      <div onPointerDown={noop} />
      <Prototype />
      {showExitFullscreenButton && (
        <Styles.ExitFullscreenButton
          aria-label="Exit fullscreen"
          variant="primary-untinted"
          size="40"
          onClick={onExitFullscreenButtonClick}
        >
          <Styles.ArrowsIcon width={24} />
        </Styles.ExitFullscreenButton>
      )}
    </Styles.Container>
  )
}

/**
 * Update the URL to always reflect the artboard id of the current prototype navigation item.
 * (The actual prototype navigation happens inside the prototype internal state and we only update the
 * URL so that users can copy the link to a specific artboard).
 */
function useSyncPrototypeNavigationWithAppRouting() {
  const history = useHistory()
  const { artboardUUID, shareID } = useParams<RouteParams<'PROTOTYPE_VIEW'>>()
  const { getPathname } = useVersioning()

  const {
    currentScreen: { screenArtboardId },
  } = useNavigation()

  useEffect(() => {
    if (screenArtboardId !== artboardUUID) {
      const newUrl = getPathname({
        routeKey: 'PROTOTYPE_VIEW',
        routeParams: {
          shareID: shareID,
          artboardUUID: screenArtboardId,
        },
      })
      history.replace({
        pathname: newUrl,
        search: history.location.search,
      })
    }
  }, [screenArtboardId, getPathname, history, artboardUUID, shareID])
}
